/**
 * Calcolo delle licenze (Preventivatore, proposta §4): dal numero di licenze
 * (PAX) alle voci del preventivo, sul listino a scaglioni.
 *
 * Regole fissate con Alessandro il 23 e 24/09/2026, dal VBA di oggi:
 * - il **setup** si paga a gradini: il primo scaglione con soglia ≥ PAX;
 * - le **licenze** si pagano a fasce marginali, come gli scaglioni IRPEF;
 * - dall'**ultimo scaglione in su** vale invece la tariffa unitaria di quello
 *   scaglione su tutte le licenze;
 * - il **premium** è sempre compreso, non si sconta e non fa riga a sé: sta
 *   dentro il costo annuo delle licenze;
 * - il **rinnovo** è una riga sola: licenze scontate più premium, mai il setup;
 * - **sotto la prima soglia non si opera**;
 * - il primo periodo può durare **da 1 a 12 mesi** (2.29, 21/10/2026): licenze
 *   e premium in proporzione, costo annuo × mesi ÷ 12; il setup resta intero e
 *   il rinnovo è sempre di 12 mesi.
 *
 * Funzioni pure su centesimi interi (money.ts): quello che si legge è quello
 * che finisce nel preventivo.
 */
import { formatMoney, formatPercent, formatQuantity, fromCents, toCents, toHundredths, } from './money.js';
/** I mesi del primo periodo: da 1 a 12, l'anno intero quando mancano. */
export function licenceMonths(months) {
    return months !== null &&
        months !== undefined &&
        Number.isInteger(months) &&
        months >= 1 &&
        months <= 12
        ? months
        : 12;
}
/** «6 mesi», «1 mese». */
export function monthsLabel(months) {
    return months === 1 ? '1 mese' : `${months} mesi`;
}
/** Scaglioni in ordine di soglia: il listino si legge sempre così. */
export function sortTiers(tiers) {
    return [...tiers].sort((a, b) => a.pax - b.pax);
}
/** Il numero minimo di licenze per cui si fa una proposta: la prima soglia. */
export function minimumPax(tiers) {
    return sortTiers(tiers)[0]?.pax ?? 0;
}
/** Perché non si può calcolare, in italiano; null quando si può. */
export function licenceError(pax, tiers) {
    if (tiers.length === 0) {
        return 'Il listino licenze è vuoto: caricalo da Configurazione → Tabelle';
    }
    if (!Number.isInteger(pax) || pax <= 0)
        return 'Scrivi quante licenze servono';
    const minimum = minimumPax(tiers);
    if (pax < minimum)
        return `Sotto le ${formatQuantity(minimum)} licenze non si fanno proposte`;
    return null;
}
/**
 * Calcolo del preventivo licenze. `tiers` è il listino in vigore; l'errore di
 * licenceError() va gestito prima, qui si assume che si possa calcolare.
 */
export function licenceQuote(request, tiers) {
    const list = sortTiers(tiers);
    const last = list.at(-1);
    const pax = request.pax;
    const tier = list.find((item) => item.pax >= pax) ?? last;
    const warnings = [];
    const setupCents = request.withSetup === false ? 0 : toCents(tier.setup);
    const extra = request.withSetup === false ? null : (request.extraSetup ?? null);
    const extraCents = extra ? toCents(extra.amount) : 0;
    // Dall'ultimo scaglione in su: tariffa unitaria di quello scaglione su tutte
    // le licenze (decisione del 24/09/2026). Sotto: fasce marginali.
    let licencesCents;
    if (pax >= last.pax) {
        licencesCents = pax * toCents(last.perLicence);
        if (pax > last.pax) {
            warnings.push(`Oltre l’ultimo scaglione del listino (${formatQuantity(last.pax)} licenze): tutte le licenze sono calcolate a ${formatMoney(last.perLicence)} l’una.`);
        }
    }
    else {
        let left = pax;
        let total = 0;
        for (const item of list) {
            if (left <= 0)
                break;
            const take = Math.min(left, item.band);
            total += take * toCents(item.perLicence);
            left -= take;
        }
        licencesCents = total;
    }
    // Il premium è sempre compreso e sta dentro il costo annuo delle licenze.
    const premiumCents = pax * toCents(tier.premium);
    const yearCents = licencesCents + premiumCents;
    // Il primo periodo in proporzione ai mesi; il rinnovo resta di un anno.
    const months = licenceMonths(request.months);
    const periodLicences = Math.round((licencesCents * months) / 12);
    const periodPremium = Math.round((premiumCents * months) / 12);
    const periodCents = periodLicences + periodPremium;
    const year1Cents = setupCents + extraCents + periodCents;
    const discount = request.renewalDiscount ?? null;
    const renewing = discount !== null && discount !== '';
    const discountedLicences = renewing
        ? Math.round((licencesCents * (10_000 - toHundredths(discount))) / 10_000)
        : 0;
    const year2Cents = renewing ? discountedLicences + premiumCents : 0;
    const percent = renewing ? toHundredths(discount) / 100 : 0;
    const lines = [];
    if (setupCents > 0) {
        lines.push({
            key: 'setup',
            label: `Setup — ${formatQuantity(pax)} licenze`,
            amount: fromCents(setupCents),
            premium: '0.00',
        });
    }
    if (extra && extraCents > 0) {
        lines.push({
            key: 'setup_extra',
            label: `Setup avanzato — ${extra.description.trim()}`,
            amount: fromCents(extraCents),
            premium: '0.00',
            mergeUp: true,
        });
    }
    lines.push({
        key: 'licences',
        label: `Licenze ${months === 12 ? '1° anno' : monthsLabel(months)} — ${formatQuantity(pax)} licenze, premium compreso`,
        amount: fromCents(periodCents),
        premium: fromCents(periodPremium),
    });
    if (renewing) {
        lines.push({
            key: 'renewal',
            label: percent > 0
                ? `Rinnovo 2° anno — ${formatQuantity(pax)} licenze, premium compreso, sconto ${formatPercent(percent)}`
                : `Rinnovo 2° anno — ${formatQuantity(pax)} licenze, premium compreso`,
            amount: fromCents(year2Cents),
            premium: fromCents(premiumCents),
        });
    }
    return {
        lines,
        year1: fromCents(year1Cents),
        year2: fromCents(year2Cents),
        total: fromCents(year1Cents + year2Cents),
        summary: {
            pax,
            setup: fromCents(setupCents),
            perLicenceYear: fromCents(Math.round(yearCents / pax)),
            perLicenceMonth: fromCents(Math.round(yearCents / pax / 12)),
            months,
            perLicencePeriod: fromCents(Math.round(periodCents / pax)),
        },
        tier,
        warnings,
    };
}
//# sourceMappingURL=licences.js.map